import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import AddOutlined from '@mui/icons-material/AddOutlined';
import PersonAddAltOutlined from '@mui/icons-material/PersonAddAltOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import AutorenewOutlined from '@mui/icons-material/AutorenewOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import { ConfirmDialog, DataTable, EmptyState, PageHeader, StatCard, StatusChip, type DataTableColumn } from '@routes-plan/ui';
import { Loan } from '../../models/loan';
import { Route } from '../../models/route';
import { Office } from '../../models/office';
import useAxiosInterceptor from '../AxiosInterceptor';
import PaymentModal from './modal/PaymentModal';
import RateLoanDialog from '../../components/rating/RateLoanDialog';
import UpdateDateModal from './modal/UpdateDateModal';
import ClientHistoryModal from '../reports/modals/ClientHistoryModal';
import { useSelection } from '../../components/context/SelectionContext';
import RefinanceModal from './modal/RefinanceModal';
import LoanTypeModal from './modal/LoanTypeModal';
import PermissionButton from '../../components/common/PermissionButton';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { formatCurrency } from '../../utils/format/currency';
import { overdueToneFor } from '../../utils/overdueColors';
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { RowActionGroup } from './components/RowAction';
import OfficeRouteSelects from './components/OfficeRouteSelects';
import { filterGridSx, pageSx, sectionSx, statGridSx } from './components/layout';


interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type RowId = string | number;

const LoanList: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('menu.loans.payments');
  const theme = useTheme();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saldoRestante, setSaldoRestante] = useState(0);
  const [isModalUpdateOpen, setIsModalUpdateOpen] = useState(false);
  const [isModalRefinanceOpen, setIsModalRefinaOpen] = useState(false);
  const [selectedRefinanceClient, setSelecRefinanClient] = useState<Loan | null>(null);
  const [selectedClient, setSelectedClient] = useState<Loan | null>(null);
  // Préstamo que se acaba de pagar: se pide calificar al cliente.
  const [rateClosed, setRateClosed] = useState<{ loanId: number; clientName: string | null } | null>(null);
  const [selectedHistoryClient, setSelectedHistoryClient] = useState<any | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [dateClient, setDateClient] = useState<Loan | null>(null);
  const { office_id: officeId, setOffice_id: setOfficeId, collect_id: routeId, setCollect_id: setRouteId, user_id: userId, setUser_id: setUserId } = useSelection();
  const [refresh, setRefresh] = useState(false);
  const [selectedIds, setSelectedIds] = useState<RowId[]>([]);
  const [isModalConstantOpen, setIsModalConstantOpen] = useState(false);
  /** Préstamos a eliminar, a la espera de confirmación. */
  const [pendingDelete, setPendingDelete] = useState<RowId[] | null>(null);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const { canCreate: canCreateClient } = useResourcePermissions('/admin/clients');
  const {
    canCreate,
    canDelete,
    canShowLocation,
    canAllowDiscount,
    canAllowManualDiscount,
    canAllowAutomaticDiscount,
    canPay,
    canRefinance,
    canUpdateConstant,
    canDeleteSelected,
    canPdf,
  } = useResourcePermissions('/loans/payments');

  useAxiosInterceptor();
  /** Evita aplicar datos de una petición obsoleta si cambió la sede o la ruta antes de que respondiera. */
  const loadSeqRef = useRef(0);

  const [modalData, setModalData] = useState<{
    loanId: number; balance: number,
    totalValue: number; interestRate: number;
    refinanceCount: number; term: number,
    userId: string, startDate: string, initialValue: number
  } | null>(null);

  useEffect(() => {
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current;

    const fetchData = async () => {
      showLoading();
      try {
        const { data: userData } = await axios.get('user');
        if (isStale()) return;
        setUserId(userData.data.id);

        const hasRoute = String(routeId ?? '').trim() !== '';
        const hasOffice = String(officeId ?? '').trim() !== '';
        setLoadingLoans(hasRoute);

        const [officesSettled, loansSettled, officeRoutesSettled] = await Promise.allSettled([
          axios.get('offices'),
          hasRoute ? axios.get(`routes/${routeId}/loans`) : Promise.resolve(null),
          hasOffice ? axios.get(`offices/${officeId}/routes`) : Promise.resolve(null),
        ]);

        if (isStale()) return;

        if (officesSettled.status === 'fulfilled' && officesSettled.value?.data?.data) {
          setOffices(officesSettled.value.data.data);
        } else if (officesSettled.status === 'rejected') {
          console.error('Error cargando sedes:', officesSettled.reason);
        }

        if (officeRoutesSettled.status === 'fulfilled' && officeRoutesSettled.value?.data != null) {
          const raw = officeRoutesSettled.value.data;
          setOfficeRoutes(Array.isArray(raw) ? raw : []);
        } else if (officeRoutesSettled.status === 'rejected') {
          console.error('Error cargando rutas de la sede:', officeRoutesSettled.reason);
        }

        setSelectedIds([]);
        if (!hasRoute) {
          setLoans([]);
        } else if (loansSettled.status === 'fulfilled' && loansSettled.value) {
          const res = loansSettled.value as { data?: { data?: unknown } };
          const list = res.data?.data;
          const listArray = Array.isArray(list) ? (list as Loan[]) : [];
          setLoans(listArray);
          if (listArray.length > 0) {
            showAlert(t('loans.loadedOk'), 'info');
          } else {
            showAlert(t('loans.noRecordsFound'), 'warning');
          }
        } else {
          setLoans([]);
          const reason = loansSettled.status === 'rejected' ? loansSettled.reason : null;
          console.error('Error cargando préstamos de la ruta:', reason);
          showAlert(
            t('loans.loadLoansError'),
            'error',
          );
        }
      } catch (error) {
        if (!isStale()) {
          console.error('Error en carga de abonos:', error);
          showAlert(t('loans.pageLoadError'), 'error');
        }
      } finally {
        if (!isStale()) {
          setLoadingLoans(false);
          hideLoading();
        }
      }
    };

    void fetchData();
  }, [routeId, officeId, refresh]);

  const handleOpenModal = (client: Loan) => {
    setSelectedClient(client);
    setIsModalOpen(true);
    setSaldoRestante(0)
  };

  const handleOpenHistoryModal = (client: Loan) => {
    setSelectedHistoryClient({
      id: client.client?.id || 0,
      routeId: client.route?.id || 0,
      routeName: client.route?.name || '',
      cedula: client.client?.idNumber || '',
      nombre: client.client?.fullName || '',
      direccion: client.client?.mainAddress || '',
      telefono: client.client?.phone || '',
      oficio: client.client?.job || '',
      celular: client.client?.mobilePhone || '',
      backupAlertActive: Boolean(client.client?.backupAlertActive),
      backups: client.client?.backups || [],
    });
    setIsHistoryModalOpen(true);
  };

  const handleOpenRefinanceModal = (client: Loan, row: any) => {
    setSelecRefinanClient(client);
    setModalData({
      loanId: row.id,
      totalValue: row.totalValue,
      interestRate: row.loanTypeInterestRate,
      term: row.termDays,
      refinanceCount: row.refinanceCount,
      startDate: row.date,
      initialValue: row.totalValue,
      userId,
      balance: row.balance,
    });
    setIsModalRefinaOpen(true);
  };

  const handleCloseUpdateModal = () => {
    setIsModalUpdateOpen(false);
    setDateClient(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
    setDateClient(null);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedHistoryClient(null);
  };

  const handleCloseRefinanceModal = () => {
    setIsModalRefinaOpen(false);
    setSelecRefinanClient(null);
    setDateClient(null);
    setModalData(null);
  };

  const handleRefresh = () => {
    setRefresh(prev => !prev);
  };

  /** Pide confirmación para eliminar un préstamo o los seleccionados. */
  const handleDeleteSelected = (idToDelete?: number) => {
    const idsToDelete = idToDelete ? [idToDelete] : selectedIds;

    if (idsToDelete.length === 0) {
      showAlert(t('loans.selectToDelete'), "warning");
      return;
    }
    setPendingDelete(idsToDelete);
  };

  const confirmDelete = async () => {
    const idsToDelete = pendingDelete ?? [];
    setPendingDelete(null);
    if (idsToDelete.length === 0) return;

    showLoading();
    try {
      await axios.post('/loans/bulk-delete', { ids: idsToDelete });
      showAlert(t('loans.deletedManyOk'), "success");
      setSelectedIds((prev) => prev.filter((id) => !idsToDelete.includes(id)));
      handleRefresh();
    } catch (error) {
      showAlert(t('common.deleteError'), "error");
      console.error(error);
    } finally {
      hideLoading();
    }
  };

  // Sedes de objeto real: no se muestra el interés (la constante solo da el plazo), sino los días.
  const isRealOffice = Boolean(
    (offices as any[]).find((o) => String(o.id) === String(officeId ?? ''))?.businessModel?.allowProductLoans,
  );

  const rows = useMemo(() => {
    if (!Array.isArray(loans)) return [];
    const ordered = [...loans].sort((a, b) => {
      const pa = a.position != null ? Number(a.position) : Number.POSITIVE_INFINITY;
      const pb = b.position != null ? Number(b.position) : Number.POSITIVE_INFINITY;
      return pa - pb;
    });
    return ordered.map((loan, index) => {
      const client = loan.client;
      const loanType = loan.loanType;
      const paymentsForDate = loan.paymentsForDate ?? { lastPaymentTime: '00:00:00', totalValue: '' };
      return {
        ...loan,
        id: loan.id,
        position: loan.position,
        positionOrdinal: index + 1,
        idNumber: client?.idNumber ?? '',
        fullName: client?.fullName ?? '—',
        value: loan.value,
        installmentValue: loan.installmentValue,
        totalValue: loan.totalValue,
        interestLabel: `${loan.interestRate ?? ''}% ${loanType?.name ?? ''}`,
        loanTypeId: loanType?.id,
        loanTypeName: loanType?.name ?? '',
        term: loanType?.term,
        refinanceCount: loan.refinanceCount,
        loanTypeInterestRate: loanType?.interestRate,
        date: loan.date,
        hour: paymentsForDate.lastPaymentTime || '00:00:00',
        termDays: loan.termDays,
        daysLate: loan.daysLate,
        balance: loan.balance,
        paidToday: Number(paymentsForDate.totalValue ?? 0),
        state: loan.state,
      };
    });
  }, [loans]);
  type LoanRow = (typeof rows)[number];

  // Totales de la ruta (antes eran la última fila de la tabla).
  const totals = useMemo(
    () => ({
      totalValue: rows.reduce((sum, row) => sum + (Number(row.totalValue) || 0), 0),
      balance: rows.reduce((sum, row) => sum + (Number(row.balance) || 0), 0),
      paidToday: rows.reduce((sum, row) => sum + (Number(row.paidToday) || 0), 0),
      paidCount: rows.filter((row) => Number(row.paidToday) > 0).length,
    }),
    [rows],
  );

  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));
  const someSelected = !allSelected && rows.some((row) => selectedIds.includes(row.id));
  const toggleRow = (id: RowId) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(allSelected ? [] : rows.map((row) => row.id));

  const columns: DataTableColumn<LoanRow>[] = [
    {
      id: 'fullName',
      header: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {canDeleteSelected ? (
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
              inputProps={{ 'aria-label': t('loans.selectAll') }}
              sx={{ p: 0.5 }}
            />
          ) : null}
          {t('loans.client')}
        </Box>
      ),
      csvHeader: t('loans.client'),
      value: (row) => row.fullName,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {canDeleteSelected ? (
            <Checkbox
              size="small"
              checked={selectedIds.includes(row.id)}
              onChange={() => toggleRow(row.id)}
              inputProps={{ 'aria-label': t('loans.selectRow', { name: row.fullName }) }}
              sx={{ p: 0.5 }}
            />
          ) : null}
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
            {row.fullName}
          </Box>
        </Box>
      ),
    },
    { id: 'positionOrdinal', header: t('loans.position'), value: (row) => row.positionOrdinal, align: 'right', hideBelow: 'sm' },
    { id: 'idNumber', header: t('common.idNumber'), value: (row) => row.idNumber, hideBelow: 'md' },
    {
      id: 'totalValue',
      header: t('loans.loanValue'),
      align: 'right',
      value: (row) => Number(row.totalValue ?? 0),
      render: (row) => formatCurrency(Number(row.totalValue ?? 0)),
    },
    isRealOffice
      ? {
          id: 'termDays',
          header: t('loans.days'),
          align: 'right',
          hideBelow: 'md',
          value: (row: LoanRow) => Number(row.termDays ?? 0),
          render: (row: LoanRow) => (row.termDays ? t('loans.daysCount', { n: row.termDays }) : ''),
        }
      : { id: 'interestLabel', header: t('loans.interest'), hideBelow: 'md', value: (row: LoanRow) => row.interestLabel },
    { id: 'date', header: t('common.date'), value: (row) => row.date, hideBelow: 'md' },
    { id: 'hour', header: t('loans.time'), value: (row) => row.hour, hideBelow: 'md' },
    {
      id: 'balance',
      header: t('loans.balance'),
      align: 'right',
      value: (row) => Number(row.balance ?? 0),
      render: (row) => formatCurrency(Number(row.balance ?? 0)),
    },
    {
      id: 'daysLate',
      header: t('loans.daysLate'),
      align: 'center',
      value: (row) => Number(row.daysLate || 0),
      render: (row) => {
        const days = Number(row.daysLate || 0);
        // Semáforo de mora con los colores de la empresa (portal › «Mi empresa»).
        const tone = overdueToneFor(days);
        return (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              height: 24,
              px: 0.75,
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              backgroundColor: tone ? tone.background : 'transparent',
              color: tone ? tone.text : theme.palette.text.primary,
            }}
          >
            {days}
          </Box>
        );
      },
    },
    {
      id: 'paidToday',
      header: t('loans.paymentValue'),
      align: 'right',
      value: (row) => Number(row.paidToday || 0),
      render: (row) => (
        <StatusChip
          label={formatCurrency(Number(row.paidToday || 0))}
          tone={Number(row.paidToday || 0) > 0 ? 'info' : 'danger'}
        />
      ),
    },
  ];

  const hasRoute = String(routeId ?? '').trim() !== '';

  return (
    <Box sx={pageSx}>
      <PageHeader
        title={t('menu.loans.payments')}
        description={t('loans.listDescription')}
        actions={
          <>
            <PermissionButton
              allowed={canDeleteSelected}
              deniedTooltip={t('loans.noPermissionDeleteSelected')}
              tooltip={t('loans.deleteSelected')}
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineOutlined />}
              disabled={selectedIds.length === 0}
              onClick={() => handleDeleteSelected()}
            >
              {selectedIds.length > 0 ? t('loans.deleteSelectedCount', { count: selectedIds.length }) : t('loans.deleteSelected')}
            </PermissionButton>
            <PermissionButton
              allowed={canCreateClient}
              deniedTooltip={t('loans.noPermissionCreateClient')}
              variant="outlined"
              startIcon={<PersonAddAltOutlined />}
              component={NavLink}
              to="/admin/clients/new"
            >
              {t('loans.addClient')}
            </PermissionButton>
            <PermissionButton
              allowed={canCreate}
              deniedTooltip={t('loans.noPermissionAddLoan')}
              variant="contained"
              startIcon={<AddOutlined />}
              component={NavLink}
              to="/loans/new"
            >
              {t('loans.newLoan')}
            </PermissionButton>
          </>
        }
      />

      <Paper variant="outlined" sx={sectionSx}>
        <Box sx={filterGridSx}>
          <OfficeRouteSelects
            offices={offices}
            officeId={officeId}
            onOfficeChange={setOfficeId}
            routes={officeRoutes}
            routeId={routeId}
            onRouteChange={setRouteId}
          />
        </Box>
      </Paper>

      {hasRoute && rows.length > 0 ? (
        <Box sx={statGridSx}>
          <StatCard label={t('loans.statLoans')} value={rows.length} hint={t('loans.statPaidCount', { count: totals.paidCount })} />
          <StatCard label={t('loans.statTotalValue')} value={formatCurrency(totals.totalValue)} />
          <StatCard label={t('loans.statBalance')} value={formatCurrency(totals.balance)} />
          <StatCard label={t('loans.statPaidToday')} value={formatCurrency(totals.paidToday)} />
        </Box>
      ) : null}

      <DataTable<LoanRow>
        columns={columns}
        rows={hasRoute ? rows : []}
        getRowId={(row) => row.id}
        loading={loadingLoans}
        searchable
        searchPlaceholder={t('loans.searchPlaceholder')}
        exportFileName={t('loans.exportFileName')}
        ariaLabel={t('menu.loans.payments')}
        rowActions={(row) => (
          <RowActionGroup
            moreLabel={t('loans.moreActions')}
            pinned={['pay', 'history']}
            items={[
              {
                key: 'pay',
                label: t('loans.addPayment'),
                icon: <PaidOutlined fontSize="small" />,
                color: 'primary',
                allowed: canPay,
                deniedLabel: t('loans.noPermissionPay'),
                onClick: () => handleOpenModal(row),
              },
              { key: 'history', label: t('loans.history'), icon: <HistoryOutlined fontSize="small" />, onClick: () => handleOpenHistoryModal(row) },
              {
                key: 'location',
                label: t('loans.viewLocation'),
                icon: <PlaceOutlined fontSize="small" />,
                allowed: canShowLocation,
                deniedLabel: t('loans.noPermissionLocation'),
                href: `https://www.google.com/maps?q=${(row as any).location}`,
              },
              {
                key: 'type',
                label: t('loans.changeLoanType'),
                icon: <TuneOutlined fontSize="small" />,
                allowed: canUpdateConstant,
                deniedLabel: t('loans.noPermissionLoanType'),
                onClick: () => {
                  setSelectedClient(row);
                  setIsModalConstantOpen(true);
                },
              },
              {
                key: 'refinance',
                label: t('loans.refinance'),
                icon: <AutorenewOutlined fontSize="small" />,
                allowed: canRefinance,
                deniedLabel: t('loans.noPermissionRefinance'),
                onClick: () => handleOpenRefinanceModal(row, row),
              },
              {
                key: 'delete',
                label: t('common.delete'),
                icon: <DeleteOutlineOutlined fontSize="small" />,
                color: 'error',
                allowed: canDelete,
                deniedLabel: t('loans.noPermissionDeleteLoans'),
                onClick: () => handleDeleteSelected(row.id),
              },
            ]}
          />
        )}
        emptyState={
          <EmptyState
            dense
            icon={<ReceiptLongOutlined />}
            title={hasRoute ? t('loans.emptyTitle') : t('loans.selectRouteTitle')}
            description={hasRoute ? t('loans.emptyDescription') : t('loans.selectRouteDescription')}
          />
        }
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        variant="danger"
        title={t('loans.confirmDeleteTitle')}
        message={t('loans.confirmDeleteMany', { count: pendingDelete?.length ?? 0 })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Modales */}
      <PaymentModal
        open={isModalOpen}
        onClose={handleCloseModal}
        client={selectedClient}
        clientId={selectedClient?.id}
        canAllowDiscount={canAllowDiscount}
        canAllowManualDiscount={canAllowManualDiscount}
        canAllowAutomaticDiscount={canAllowAutomaticDiscount}
        canPdf={canPdf}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        onPaymentSuccess={handleRefresh}
        onLoanClosed={(loanId, clientName) => setRateClosed({ loanId, clientName })}
      />
      <RateLoanDialog
        open={Boolean(rateClosed)}
        loanId={rateClosed?.loanId ?? null}
        clientName={rateClosed?.clientName}
        justClosed
        onClose={() => setRateClosed(null)}
        showAlert={showAlert}
      />
      <LoanTypeModal
        open={isModalConstantOpen}
        onClose={() => setIsModalConstantOpen(false)}
        client={selectedClient}
        officeId={officeId}
        routeId={routeId}
        userId={userId}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        onPaymentSuccess={() => {
          setIsModalConstantOpen(false);
          handleRefresh();
        }}
      />
      <RefinanceModal
        open={isModalRefinanceOpen}
        onClose={handleCloseRefinanceModal}
        client={selectedRefinanceClient}
        clientId={selectedRefinanceClient?.id}
        modalData={modalData}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        onPaymentSuccess={handleRefresh}
      />
      <UpdateDateModal
        open={isModalUpdateOpen}
        onClose={handleCloseUpdateModal}
        client={dateClient}
        clientId={dateClient?.id}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <ClientHistoryModal
        open={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        clientData={selectedHistoryClient}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </Box>
  );
}

export default LoanList;
